import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight, ChevronDown, Heart, Sun, Waypoints, Moon, Sparkles,
  ScanSearch, Waves, MessageCircle, BadgeCheck, Star,
} from "lucide-react";
import { MaskedLine, FadeIn, Reveal } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { useLang } from "../LanguageContext";
import { IMAGES, CONTACT, waLink } from "../data/site";

const ICONS = { heart: Heart, sun: Sun, waypoints: Waypoints, moon: Moon };
const OFFER_ICONS = [ScanSearch, Waves, Waypoints, Moon];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.02, 1.14]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);
  const { s } = useLang();

  return (
    <section ref={ref} data-testid="hero-section" className="relative flex min-h-[100svh] items-center overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <motion.img
          src={IMAGES.hero}
          alt={s.hero.imgAlt}
          className="h-full w-full object-cover"
          animate={{ scale: [1.04, 1.09, 1.04] }}
          transition={{ duration: 44, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.img
          src={IMAGES.clouds}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover opacity-50"
          animate={{ x: ["-1.8%", "1.8%", "-1.8%"], scale: 1.08 }}
          transition={{ duration: 26, repeat: Infinity, ease: "easeInOut" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-obsidian/50 via-obsidian/10 to-obsidian" />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(9,12,16,0.72),rgba(9,12,16,0.3)_55%,rgba(9,12,16,0.05))]" />
        <div className="absolute inset-0 bg-[radial-gradient(75%_55%_at_50%_42%,rgba(9,12,16,0.12),rgba(9,12,16,0.5))]" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-6xl px-5 pt-28 pb-16 sm:px-8">
        <FadeIn delay={0.2}>
          <p className="eyebrow flex items-center gap-3">
            <Sparkles className="h-3.5 w-3.5 text-gold" />
            {s.hero.eyebrow}
          </p>
        </FadeIn>

        <h1 className="mt-6 font-serif text-[clamp(3.2rem,10vw,7.5rem)] font-light leading-[0.98] tracking-tight text-white [filter:drop-shadow(0_2px_18px_rgba(9,12,16,0.95))_drop-shadow(0_0_2px_rgba(9,12,16,0.9))]">
          <MaskedLine delay={0.35}>{s.hero.titleA}</MaskedLine>
          <MaskedLine delay={0.55} className="italic text-gold-light">{s.hero.titleB}</MaskedLine>
        </h1>

        <FadeIn delay={1.1} className="mt-8 max-w-xl">
          <p className="text-base leading-relaxed text-white/75 sm:text-lg">{s.hero.paragraph}</p>
        </FadeIn>

        <FadeIn delay={1.3} className="mt-10 flex flex-wrap items-center gap-4">
          <a href={waLink(s.wa.defaultMsg)} target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp-cta" className="btn-mint">
            <MessageCircle className="h-4 w-4" /> {s.hero.ctaWhatsapp}
          </a>
          <Link to="/accompagnements" data-testid="hero-offers-cta" className="btn-gold">
            {s.hero.ctaOffers} <ArrowRight className="h-4 w-4" />
          </Link>
        </FadeIn>
      </motion.div>

      <motion.div
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 text-white/50"
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
      >
        <ChevronDown className="h-6 w-6" />
      </motion.div>
    </section>
  );
};

const CHAKRAS = ["#E5484D", "#FF9A3C", "#F3D34A", "#2EE9A6", "#3AA0FF", "#6366F1", "#C084FC"];

const ChakraFrequencyCircle = ({ className = "" }) => (
  <svg viewBox="0 0 400 400" className={className} aria-hidden="true">
    <circle cx="200" cy="200" r="192" fill="none" stroke="rgba(212,175,55,0.35)" strokeWidth="1" />
    <circle cx="200" cy="200" r="158" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="1" strokeDasharray="3 7" />
    <circle cx="200" cy="200" r="118" fill="none" stroke="rgba(139,124,255,0.32)" strokeWidth="1" />
    <circle cx="200" cy="200" r="70" fill="none" stroke="rgba(212,175,55,0.28)" strokeWidth="1" strokeDasharray="1 5" />
    {Array.from({ length: 60 }).map((_, i) => (
      <line
        key={i}
        x1="200" y1="14"
        x2="200" y2={i % 5 === 0 ? "30" : "22"}
        stroke="rgba(212,175,55,0.4)"
        strokeWidth="1"
        transform={`rotate(${i * 6} 200 200)`}
      />
    ))}
    {CHAKRAS.map((c, i) => {
      const a = ((-90 + (i * 360) / 7) * Math.PI) / 180;
      const x = 200 + 158 * Math.cos(a);
      const y = 200 + 158 * Math.sin(a);
      return (
        <g key={c}>
          <circle cx={x} cy={y} r="10" fill={c} opacity="0.25" />
          <circle cx={x} cy={y} r="4.5" fill={c} />
        </g>
      );
    })}
    <circle cx="200" cy="200" r="3" fill="rgba(243,229,171,0.9)" />
  </svg>
);

const About = () => {
  const { s } = useLang();
  return (
    <section data-testid="about-section" className="relative mx-auto grid w-full max-w-6xl items-center gap-12 overflow-hidden px-5 py-24 sm:px-8 lg:grid-cols-[0.6fr_1.4fr] lg:py-32">
      <Reveal className="relative">
        <ChakraFrequencyCircle className="absolute -top-[30%] -left-[38%] h-[172%] w-auto opacity-55 animate-spin-slower" />
        <div className="absolute -inset-6 rounded-[2.5rem] bg-[radial-gradient(60%_60%_at_50%_30%,rgba(212,175,55,0.22),transparent)] blur-2xl" />
        <div className="relative z-10 w-full max-w-[240px] translate-x-4 overflow-hidden rounded-[2rem] border border-gold/20 shadow-[0_30px_80px_rgba(0,0,0,0.55)] sm:max-w-[300px] lg:max-w-[340px] lg:translate-x-14 lg:translate-y-10">
          <img
            src={IMAGES.portrait}
            alt={`${s.about.titleA} ${s.about.titleB}, ${s.about.certTitle}`}
            data-testid="practitioner-portrait"
            className="aspect-[4/5] w-full object-cover"
          />
          <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-obsidian/85 to-transparent" />
        </div>
      </Reveal>

      <div>
        <Reveal><p className="eyebrow">{s.about.eyebrow}</p></Reveal>
        <Reveal delay={0.08}>
          <h2 className="mt-5 font-serif text-4xl font-light leading-tight text-white sm:text-5xl">
            {s.about.titleA} <span className="italic text-halo">{s.about.titleB}</span>
          </h2>
        </Reveal>
        <Reveal delay={0.14}>
          <p className="mt-6 leading-relaxed text-white/70">{s.about.p1}</p>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-4 leading-relaxed text-white/55">{s.about.p2}</p>
        </Reveal>
        <Reveal delay={0.26}>
          <blockquote className="mt-8 border-l-2 border-gold/50 pl-6 font-serif text-xl italic leading-snug text-gold-light sm:text-2xl">
            « {s.about.quote} »
          </blockquote>
        </Reveal>
        <Reveal delay={0.32} className="mt-10 flex items-center gap-5">
          <div data-testid="certification-badge" className="w-fit shrink-0 rounded-full bg-white p-1.5 shadow-[0_10px_30px_rgba(0,0,0,0.45)]">
            <img src={IMAGES.badge} alt={s.about.badgeAlt} className="h-24 w-24 rounded-full sm:h-28 sm:w-28" />
          </div>
          <div>
            <p className="flex items-center gap-2 text-sm font-semibold text-white">
              <BadgeCheck className="h-4 w-4 shrink-0 text-gold" /> {s.about.certTitle}
            </p>
            <p className="mt-1 text-xs leading-relaxed text-white/55">{s.about.certSub}</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const Dimensions = () => {
  const { s } = useLang();
  return (
    <section data-testid="dimensions-section" className="mx-auto w-full max-w-6xl px-5 py-16 sm:px-8">
      <Reveal><p className="eyebrow text-center">{s.dimensions.eyebrow}</p></Reveal>
      <Reveal delay={0.08}>
        <h2 className="mx-auto mt-5 max-w-2xl text-center font-serif text-3xl font-light text-white sm:text-4xl">
          {s.dimensions.titleA} <span className="italic text-veil">{s.dimensions.titleB}</span> {s.dimensions.titleC}
        </h2>
      </Reveal>
      <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {s.dimensions.items.map((d, i) => {
          const Icon = ICONS[["heart", "sun", "waypoints", "moon"][i]];
          return (
            <Reveal key={d.title} delay={0.08 * i} className="h-full">
              <div data-testid={`dimension-card-${i}`} className="glass h-full rounded-3xl p-7 transition-all duration-500 hover:-translate-y-1.5 hover:border-iris/40">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-iris/30 bg-iris/10">
                  <Icon className="h-5 w-5 text-iris" />
                </div>
                <h3 className="mt-6 font-serif text-xl text-white">{d.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-white/55">{d.text}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
};

const OffersTeaser = () => {
  const { s } = useLang();
  return (
    <section data-testid="offers-teaser-section" className="mx-auto w-full max-w-6xl px-5 py-16 sm:px-8">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <Reveal><p className="eyebrow">{s.offersTeaser.eyebrow}</p></Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-5 font-serif text-3xl font-light text-white sm:text-5xl">
              {s.offersTeaser.titleA} <span className="italic text-halo">{s.offersTeaser.titleB}</span>
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.15}>
          <Link to="/accompagnements" data-testid="offers-teaser-link" className="btn-ghost">
            {s.offersTeaser.cta} <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </div>

      <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {s.offers.map((o, i) => {
          const Icon = OFFER_ICONS[i];
          return (
            <Reveal key={o.id} delay={0.1 * i} className="h-full">
              <Link
                to="/accompagnements"
                data-testid={`offer-teaser-card-${i}`}
                className="offer-card flex h-full flex-col !bg-gradient-to-b !from-white/[0.05] !to-transparent"
              >
                <Icon className="h-6 w-6 text-gold" />
                <p className="eyebrow mt-5">{o.tagline}</p>
                <h3 className="mt-3 font-serif text-xl leading-snug text-white">{o.title}</h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-white/55">{o.description.split(".")[0]}.</p>
                <p className="mt-6 font-mono text-sm tracking-wider text-gold-light">{o.price}</p>
                <p className="mt-1 font-mono text-[11px] text-white/45">{o.priceAlt}</p>
              </Link>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
};

const Testimonials = () => {
  const { s } = useLang();
  return (
    <section data-testid="testimonials-section" className="mx-auto w-full max-w-6xl px-5 py-16 sm:px-8">
      <Reveal><p className="eyebrow text-center">{s.testimonials.eyebrow}</p></Reveal>
      <Reveal delay={0.08}>
        <h2 className="mx-auto mt-5 max-w-2xl text-center font-serif text-3xl font-light text-white sm:text-4xl">
          {s.testimonials.titleA} <span className="italic text-halo">{s.testimonials.titleB}</span>
        </h2>
      </Reveal>
      <div className="mt-14 grid gap-5 md:grid-cols-3">
        {s.testimonials.items.map((t, i) => (
          <Reveal key={t.author} delay={0.1 * i} className="h-full">
            <figure
              data-testid={`testimonial-card-${i}`}
              className="glass flex h-full flex-col rounded-3xl p-7 transition-all duration-500 hover:-translate-y-1.5 hover:border-gold/25"
            >
              <div className="flex gap-1">
                {[...Array(5)].map((_, k) => (
                  <Star key={k} className="h-4 w-4 fill-gold text-gold" />
                ))}
              </div>
              <blockquote className="mt-5 flex-1 font-serif text-lg italic leading-relaxed text-white/85">
                « {t.quote} »
              </blockquote>
              <figcaption className="mt-6 flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-full border border-gold/30 bg-gold/10 font-serif text-sm text-gold-light">
                  {t.author[0]}
                </div>
                <span className="text-sm font-medium text-white/75">{t.author}</span>
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

const SoundBand = () => {
  const { s } = useLang();
  return (
    <section data-testid="sound-band" className="relative overflow-hidden">
      <img src={IMAGES.veil} alt="" className="absolute inset-0 h-full w-full object-cover opacity-50" />
      <div className="absolute inset-0 bg-gradient-to-b from-obsidian via-obsidian/60 to-obsidian" />
      <div className="relative mx-auto max-w-3xl px-5 py-24 text-center sm:px-8">
        <Reveal><p className="eyebrow">{s.sound.eyebrow}</p></Reveal>
        <Reveal delay={0.08}>
          <h2 className="mt-5 font-serif text-3xl font-light text-white sm:text-4xl">
            {s.sound.titleA} <span className="italic text-halo">{s.sound.titleB}</span>
          </h2>
        </Reveal>
        <Reveal delay={0.16}>
          <p className="mx-auto mt-5 max-w-xl text-sm leading-relaxed text-white/60 sm:text-base">{s.sound.text}</p>
        </Reveal>
      </div>
    </section>
  );
};

const FinalCta = () => {
  const { s } = useLang();
  return (
    <section data-testid="final-cta-section" className="mx-auto w-full max-w-6xl px-5 py-24 text-center sm:px-8">
      <Reveal><p className="eyebrow">{s.final.eyebrow}</p></Reveal>
      <Reveal delay={0.08}>
        <h2 className="mx-auto mt-6 max-w-3xl font-serif text-4xl font-light leading-tight text-white sm:text-5xl">
          {s.final.title}
        </h2>
      </Reveal>
      <Reveal delay={0.18} className="mt-10 flex flex-wrap items-center justify-center gap-4">
        <a href={waLink(s.wa.defaultMsg)} target="_blank" rel="noopener noreferrer" data-testid="final-whatsapp-cta" className="btn-mint">
          <MessageCircle className="h-4 w-4" /> WhatsApp — {CONTACT.phoneDisplay}
        </a>
        <Link to="/contact" data-testid="final-contact-cta" className="btn-gold">
          {s.final.ctaContact} <ArrowRight className="h-4 w-4" />
        </Link>
      </Reveal>
    </section>
  );
};

const Home = () => (
  <main>
    <Hero />
    <Marquee />
    <About />
    <Dimensions />
    <OffersTeaser />
    <Testimonials />
    <SoundBand />
    <FinalCta />
  </main>
);

export default Home;
